/**
 * ConnectionBanner — 전역 상단 백엔드/Firebase 연결 상태 배너.
 *
 * 2026-06-11: 연결이 끊겼을 때 화면이 (옛 캐시/빈 목록 등으로) 정상처럼 보여
 * 진단이 늦어지던 문제 대응. 모든 화면 최상단에 고정 배너로 연결 불가를 명확히
 * 표시한다. /api/health 를 주기적으로 폴링 (auth 면제 엔드포인트라 키 불필요).
 *
 * 상태:
 *   ok        — 배너 숨김
 *   down      — 백엔드 미응답/에러 → 빨간 배너
 *   db_down   — 백엔드는 응답하나 recipe_db 미로딩 등 → 주황 배너
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../utils";

const POLL_MS = 20000;

export default function ConnectionBanner() {
  const [status, setStatus] = useState("ok");
  const [checking, setChecking] = useState(false);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    setChecking(true);
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 8000);
    try {
      const res = await fetch(`${API_BASE_URL}/api/health`, {
        method: "GET",
        signal: ctrl.signal,
        cache: "no-store",
      });
      if (!res.ok) { setStatus("down"); return; }
      const data = await res.json();
      if (data?.status === "ok" && data?.recipe_db === "v2") setStatus("ok");
      else if (data?.status === "ok") setStatus("db_down");
      else setStatus("down");
    } catch {
      // 네트워크 실패/타임아웃/JSON 파싱 실패 = 연결 불가
      setStatus("down");
    } finally {
      clearTimeout(to);
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  if (status === "ok") return null;

  const isDown = status === "down";
  const bg = isDown ? "#b91c1c" : "#b45309";
  const title = isDown
    ? "백엔드 / Firebase 서버에 연결할 수 없습니다"
    : "서버는 응답하나 단가 데이터(recipe_db)가 로딩되지 않았습니다";
  const detail = isDown
    ? "지금 보이는 화면은 최신 데이터가 아닐 수 있습니다. 새로고침해도 계속되면 백엔드(Cloud Run)·Firebase 연결 상태를 확인하세요."
    : "잠시 후 자동 복구될 수 있습니다. 계속되면 recipe_db 배포 상태를 확인하세요.";

  return (
    <div
      role="alert"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 9999,
        width: "100%",
        background: bg,
        color: "#fff",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>⚠️</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
        <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2 }}>{detail}</div>
      </div>
      <button
        type="button"
        onClick={check}
        disabled={checking}
        style={{
          flexShrink: 0,
          background: "rgba(255,255,255,0.18)",
          border: "1px solid rgba(255,255,255,0.5)",
          color: "#fff",
          borderRadius: 6,
          padding: "6px 12px",
          fontSize: 13,
          fontWeight: 600,
          cursor: checking ? "default" : "pointer",
        }}
      >
        {checking ? "확인 중…" : "다시 확인"}
      </button>
    </div>
  );
}
